import React, { useCallback, useEffect, useMemo, useState } from 'react';

import usingStorage from 'app/utils/storage';
import { createUrl, debounce, goToSection } from 'app/utils';

import { useMenu } from 'features/menu';

import { Loading, Pace, Page } from 'components';
import { Carousel } from 'features/headers';

import { DashboardAreaCardOrganizationEnum, DashboardOrganizationTypeEnum } from '../../types';
import useSheets from '../../useSheets';
import { DashboardSubmenu } from '../../components';
import { DashboardAreaCarousel, DashboardAreaGrid } from '../../sheets';
import ProductsDetailsView from 'features/products/views/productsDetailsView/ProductsDetailsView';
import { ProductNextItems } from 'features/products';

const storage = usingStorage('localStorage');

const Dashboard = () => {
  const [loading, setLoading] = useState(true);

  // const { dashboardUuid } = useParams();

  const { setBackPathname, clearBackPathname } = useMenu();
  const { dashboard, setDashboardUsing, setDashboardForceOld, fetchDashboard } = useSheets();

  const dashboardAreas = useMemo(() => {
    return dashboard?.areas.filter((area) => {
      return !!area?.cards?.length;
    });
  }, [dashboard?.areas]);

  const getDashboard = useCallback(() => {
    setLoading(true);

    const currentDashboardUuid = storage.get('currentDashboard') || null;

    fetchDashboard(currentDashboardUuid, ({ dashboardUuid, hasDashboardUuid }) => {
      setLoading(false);

      if (!dashboardUuid) {
        storage.remove('currentDashboard');

        if (hasDashboardUuid) {
          window.location.reload();
        } else {
          setDashboardUsing(false);
          setDashboardForceOld(true);
        }
      }
    });
  }, []);

  // let currentDashboardUuid = '';
  // // if (dashboardUuid) {
  // //   currentDashboardUuid = dashboardUuid;
  // // } else {
  //   if (storageDashboardUuid) {
  //     currentDashboardUuid = storageDashboardUuid;
  //   }
  // }

  // const fetchDashboard = useCallback(async () => {
  //   setLoading(true);

  //   const currentDashboardUuid = storage.get('currentDashboard');

  //   await request<SchoolDashboardResponse>({
  //     url: currentDashboardUuid ? `/api/v1/School/Dashboard/Get/${currentDashboardUuid}` : '/api/v1/School/Dashboard/Default',
  //     baseURL: apiGatewayUrl,
  //     method: 'get',
  //   })
  //     .then((response) => {
  //       const { linkButtonsColor, textIconColors, siteTopColor, logoDarkBackgroundImageUrl, logoLightBackgroundImageUrl, faviconImageUrl } =
  //         response.data;

  //       const PRIMARY_COLOR_400 = linkButtonsColor;
  //       const PRIMARY_COLOR_200 = lightenColor(PRIMARY_COLOR_400, 10);
  //       const PRIMARY_COLOR_600 = darkenColor(PRIMARY_COLOR_400, 10);

  //       setSchoolColors({
  //         primary_200: PRIMARY_COLOR_200,
  //         primary_400: PRIMARY_COLOR_400,
  //         primary_600: PRIMARY_COLOR_600,
  //         primary_contrast: textIconColors,
  //         navbarBg: siteTopColor,
  //       });

  //       setSchoolUiSettings({
  //         logoDarkUrl: logoDarkBackgroundImageUrl,
  //         logoUrl: logoLightBackgroundImageUrl,
  //         favIconUrl: faviconImageUrl,
  //       });

  //       let link: HTMLLinkElement | null = document.querySelector("link[rel~='icon']");

  //       if (!link) {
  //         link = document.createElement('link');
  //         link.rel = 'icon';
  //         document.getElementsByTagName('head')[0].appendChild(link);
  //       }

  //       link.href = faviconImageUrl;

  //       setCurrentDashboard(response.data.uuid);
  //       setDashboard(response.data);
  //       setDashboardUsing(true);
  //     })
  //     .catch(() => {
  //       storage.remove('currentDashboard');
  //       setDashboardUsing(null);

  //       window.location.reload();
  //     })
  //     .finally(() => {
  //       setLoading(false);
  //     });
  // }, []);

  useEffect(() => {
    getDashboard();
    clearBackPathname();

    return () => {
      setBackPathname(createUrl('dashboard'));
    };
  }, []);

  const [areas, setAreas] = useState<{ areaTop: number; areaHeight: number; areaId: string }[]>([]);
  const [currentAreaIndex, setCurrentAreaIndex] = useState(0);
  const [currentAreaClicked, setCurrentAreaClicked] = useState(false);

  const goToArea = useCallback(
    (index = 0) => {
      setCurrentAreaClicked(true);
      setCurrentAreaIndex(index);

      const area = areas[index];
      setTimeout(() => {
        goToSection(area.areaId);
      }, 100);
    },
    [areas]
  );

  const checkAreas = useCallback(
    (attempt = 0) => {
      const pages: Element[] = [].slice.call(document.querySelectorAll('.dashboard-area'), 0);

      if (pages.length) {
        setAreas(
          pages.map((page) => {
            const box = page.getBoundingClientRect();

            return {
              areaId: page.id,
              areaTop: box.top,
              areaHeight: box.height,
            };
          })
        );
      } else {
        if (attempt < 3) setTimeout(() => checkAreas(attempt + 1), 250);
      }
    },
    [dashboardAreas]
  );

  useEffect(() => {
    setAreas([]);

    if (dashboardAreas?.length) checkAreas();
  }, [dashboardAreas]);

  const checkAreaOnScroll = useCallback(
    debounce(() => {
      const body = document.body;
      const documentElement = document.documentElement;

      let scrollTop = window.pageYOffset || documentElement.scrollTop || body.scrollTop;

      scrollTop = scrollTop + 50;

      const areaIndex = areas.reduce((prev, page, index) => {
        return scrollTop > page.areaTop && scrollTop < page.areaTop + page.areaHeight ? index : prev;
      }, 0);

      if (currentAreaClicked) {
        setCurrentAreaClicked(false);
      } else {
        setCurrentAreaIndex(areaIndex);
      }
    }, 200),

    [areas, currentAreaClicked]
  );

  useEffect(() => {
    window.addEventListener('scroll', checkAreaOnScroll);
    window.addEventListener('resize', checkAreaOnScroll);

    return () => {
      window.removeEventListener('scroll', checkAreaOnScroll);
      window.removeEventListener('resize', checkAreaOnScroll);
    };
  }, [areas, currentAreaClicked]);

  const currentDashboardUuid = storage.get('currentDashboard');
  if (!dashboard || dashboard.uuid !== currentDashboardUuid) return <Loading label="Por favor, aguarde." />;

  return (
    <Page title={dashboard?.name}>
      {loading && <Pace />}

      {dashboard?.dashboardOrganizationType === DashboardOrganizationTypeEnum.SingleCourse ? (
        <ProductsDetailsView
          idDashboard
          productSlug={dashboard?.productSlug!}
          productClassUuid={dashboard?.productClassUuid}
          nextItems={dashboard.nextItens}
        />
      ) : (
        <>
          {!!dashboard?.headers?.length && <Carousel headers={dashboard.headers} />}

          {!!dashboard?.nextItens && <ProductNextItems nextItems={dashboard.nextItens} />}

          {dashboardAreas?.length > 1 && <DashboardSubmenu areas={dashboardAreas} areaIndex={currentAreaIndex} onAreaClick={goToArea} />}

          {dashboardAreas.map((area, areaIndex) => {
            switch (area.dashboardAreaCardOrganization) {
              case DashboardAreaCardOrganizationEnum.HorizontalList:
                return (
                  <DashboardAreaCarousel
                    key={`sheet-${area.uuid}`}
                    dashboardVisualizationType={dashboard?.dashboardVisualizationType}
                    area={area}
                    nextSheetUuid={areaIndex < dashboardAreas.length - 1 ? dashboardAreas[areaIndex + 1]?.uuid : undefined}
                  />
                );

              case DashboardAreaCardOrganizationEnum.VerticalGrid:
                return (
                  <DashboardAreaGrid
                    key={`sheet-${area.uuid}`}
                    dashboardVisualizationType={dashboard?.dashboardVisualizationType}
                    area={area}
                    nextSheetUuid={areaIndex < dashboardAreas.length - 1 ? dashboardAreas[areaIndex + 1]?.uuid : undefined}
                  />
                );

              default:
                return <></>;
            }
          })}
        </>
      )}
    </Page>
  );
};

export default Dashboard;
